import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL = `${SITE.url}/corrections`;
const LAST_UPDATED = "July 2026";

export const Route = createFileRoute("/corrections")({
  head: () => ({
    meta: [
      { title: `Corrections policy — ${SITE.name}` },
      { name: "description", content: "How Map Observer handles errors, updates, and retractions on our reporting about Google Business Profile and local search." },
      { property: "og:title", content: "Corrections policy" },
      { property: "og:description", content: "Reporting errors, requesting corrections, and how updates are logged." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Policy</p>
        <h1 className="headline-display text-4xl mt-3">Corrections policy</h1>
        <p className="mt-4 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <section className="mt-10 space-y-4 text-sm leading-relaxed">
          <p>Map Observer is committed to accurate, evidence-based reporting on Google Business Profile, the Map Pack, and local search. When we get something wrong, we correct it promptly and transparently.</p>

          <h2 className="headline-display text-2xl mt-6">How to report an error</h2>
          <p>Email <a href="mailto:corrections@mapobserver.com" className="text-accent underline">corrections@mapobserver.com</a> with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>The article URL</li>
            <li>The specific claim you believe is incorrect</li>
            <li>A primary source supporting the correct version, if available</li>
          </ul>
          <p>We aim to acknowledge correction requests within one business day and to publish a correction (or explain why we are not making one) within three business days.</p>

          <h2 className="headline-display text-2xl mt-6">How corrections are logged</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Factual errors:</strong> the incorrect text is replaced and an "Editor's note" appears at the top of the article describing what changed and when.</li>
            <li><strong>Material updates:</strong> when a story develops (Google publishes new information, an update is confirmed, a suspension wave ends), we append an "Update" block dated to the change.</li>
            <li><strong>Retractions:</strong> if a story is fundamentally wrong, the article is replaced in place with a retraction notice explaining what we got wrong and the URL is preserved so existing links keep working.</li>
            <li><strong>Silent edits:</strong> typo fixes, grammar, and clarifications that do not change meaning are made without an editor's note.</li>
          </ul>

          <h2 className="headline-display text-2xl mt-6">Update transparency</h2>
          <p>Every published article carries a <code>dateModified</code> timestamp that reflects the most recent substantive edit. See also our <Link to="/editorial-standards" className="text-accent underline">editorial standards</Link>.</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
